import { AssessmentType, CyberAssessmentStatus } from '@@types/api.types';

type HomeDraftTechnologyLike = {
  id: string;
  name: string;
};

type HomeDraftCyberAssessmentLike = {
  id: string;
  status: CyberAssessmentStatus;
  technologyId: string;
  updatedAt: string;
};

type HomeDraftSurveyLike = {
  assessmentType: AssessmentType;
  createdAt: string;
  id: string;
  technology?: HomeDraftTechnologyLike;
};

export type HomeDraftItem<TSurvey extends HomeDraftSurveyLike, TCyberAssessment extends HomeDraftCyberAssessmentLike> =
  | {
      assessment: TCyberAssessment;
      kind: 'cyberAssessment';
      technology?: HomeDraftTechnologyLike;
    }
  | {
      kind: 'survey';
      survey: TSurvey;
    };

export const buildHomeCyberAssessmentDraftHref = (params: {
  cyberAssessmentId: string;
  technologyId: string;
}): string => {
  const { cyberAssessmentId, technologyId } = params;

  return `/technology/${technologyId}/cyber-assessment/${cyberAssessmentId}?viewFirstUnansweredQuestion=true&returnTo=${encodeURIComponent('/')}`;
};

export const buildHomeDraftItems = <
  TSurvey extends HomeDraftSurveyLike,
  TCyberAssessment extends HomeDraftCyberAssessmentLike,
>(params: {
  cyberAssessmentsByTechnologyId: Record<string, TCyberAssessment[]>;
  surveys: TSurvey[];
}): Array<HomeDraftItem<TSurvey, TCyberAssessment>> => {
  const { cyberAssessmentsByTechnologyId, surveys } = params;
  const seenCyberAssessmentIds = new Set<string>();

  const items = surveys.reduce<Array<HomeDraftItem<TSurvey, TCyberAssessment>>>((accumulator, survey) => {
    if (survey.assessmentType !== AssessmentType.CYBER) {
      accumulator.push({ kind: 'survey', survey });
      return accumulator;
    }

    const technologyId = survey.technology?.id;

    if (!technologyId) {
      return accumulator;
    }

    const draftAssessment = cyberAssessmentsByTechnologyId[technologyId]?.find(
      (assessment) => assessment.status === CyberAssessmentStatus.DRAFT,
    );

    if (!draftAssessment || seenCyberAssessmentIds.has(draftAssessment.id)) {
      return accumulator;
    }

    seenCyberAssessmentIds.add(draftAssessment.id);
    accumulator.push({
      assessment: draftAssessment,
      kind: 'cyberAssessment',
      technology: survey.technology,
    });

    return accumulator;
  }, []);

  return items.sort((left, right) => {
    const leftTimestamp = left.kind === 'cyberAssessment' ? left.assessment.updatedAt : left.survey.createdAt;
    const rightTimestamp = right.kind === 'cyberAssessment' ? right.assessment.updatedAt : right.survey.createdAt;

    return rightTimestamp.localeCompare(leftTimestamp);
  });
};
